import { Button, Card, CardBody, CardHeader, Divider, Input } from '@heroui/react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import AddressForm from '@/app/components/address-form.tsx';
import { PhoneInput } from '@/app/components/phone-input.tsx';
import toast from '@/app/toast.ts';
import {
  usePostOrganizationMutation,
  usePutOrganizationMutation,
} from '@/slices/organization-api-slice.ts';
import { emailPattern } from '../../../types/constants.ts';
import type { OrganizationModel } from '../../../types/organization.type.ts';

export interface OrganizationProps {
  organization: OrganizationModel;
}

export default function OrganizationForm({ organization }: OrganizationProps) {
  const form = useForm<OrganizationModel>({
    values: organization,
  });
  const [saveOrganization, { isLoading }] = organization?.key
    ? // biome-ignore lint/correctness/useHookAtTopLevel: TODO: fix me
      usePutOrganizationMutation()
    : // biome-ignore lint/correctness/useHookAtTopLevel: TODO: fix me
      usePostOrganizationMutation();
  const navigate = useNavigate();
  const { handleSubmit, control } = form;

  const onSubmit: SubmitHandler<OrganizationModel> = (value: OrganizationModel) => {
    saveOrganization(value)
      .unwrap()
      .then(response => {
        toast.success('Organization saved');
        navigate(`/organization/edit/${response.key}`);
      })
      .catch(() => {
        toast.error('There was an error saving the organization');
      });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Card shadow='sm' className='border border-gray-200'>
            <CardHeader className='flex gap-3'>
              <h3 className='text-lg font-semibold'>Organization Details</h3>
            </CardHeader>
            <Divider />
            <CardBody className='gap-4'>
              <Controller
                name='name'
                control={control}
                rules={{ required: 'This field is required' }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label='Name'
                    variant='bordered'
                    isRequired
                    isInvalid={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
              <AddressForm name='address' required={true} />
            </CardBody>
          </Card>

          <Card shadow='sm' className='border border-gray-200'>
            <CardHeader className='flex gap-3'>
              <h3 className='text-lg font-semibold'>Contact Information</h3>
            </CardHeader>
            <Divider />
            <CardBody className='gap-4'>
              <Controller
                name='emailAddress'
                control={control}
                rules={{
                  required: 'This field is required',
                  pattern: {
                    value: emailPattern,
                    message: 'Invalid email',
                  },
                }}
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    label='Email'
                    variant='bordered'
                    type='email'
                    isRequired
                    isInvalid={fieldState.invalid}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
              <PhoneInput name='phoneNumber' label='Phone' control={control} required />
            </CardBody>
          </Card>
        </div>
        <div className='flex pt-5'>
          <Button isLoading={isLoading} color='primary' variant='solid' size='lg' type='submit'>
            {organization ? 'Save Details' : 'Create'}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
